import store from '../store';
import { PublicClientApplication, InteractionRequiredAuthError, SilentRequest, AccountInfo } from '@azure/msal-browser';
import { b2cPolicies, msalConfig, loginRequest, tokenRequest, buildResetRoute } from './authConfig';

let accountId = '';
let accountDetails = {};
let username = '';
let accessToken = '';

const myMSALObj = new PublicClientApplication(msalConfig);

myMSALObj.handleRedirectPromise()
    .then((response: any) => {
        if (response) {
            /**
             * For the purpose of setting an active account for UI update, we want to consider only the auth response resulting
             * from SUSI flow. "tfp" claim in the id token tells us the policy (NOTE: legacy policies may use "acr" instead of "tfp").
             * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
             */
            // NOTE: custom policies seem to want to use "acr" too
            if (response.idTokenClaims.tfp) {
                if (response.idTokenClaims.tfp.toUpperCase() === b2cPolicies.names.signUpSignIn.toUpperCase()) {
                    handleResponse(response);
                }
            } else if (response.idTokenClaims.acr) {
                if (response.idTokenClaims.acr.toUpperCase() === b2cPolicies.names.signUpSignIn.toUpperCase()) {
                    handleResponse(response);
                }
            }
        }
    })
    .catch((error) => {
        console.log(error);
        // handle the forgotten PW error code
        if (error.name === 'ServerError' && error.message.indexOf('AADB2C90118') !== -1) {
            window.location.href = buildResetRoute();
        }
    });

const handleResponse = async (response: any) => {
    /**
     * To see the full list of response object properties, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#response
     */

    if (response !== null) {
        setAccessToken(response.accessToken);
        setAccount(response.account);
    } else {
        selectAccount();
    }
};

const setAccount = (account: any) => {
    accountId = account.homeAccountId;
    username = account.username;
    accountDetails = account;

    if (store) {
        store.dispatch('setIsLoggedIn', true);
        store.dispatch('initFavourites');
        store.dispatch('initFilters');
    }
};

const setAccessToken = (token: any) => {
    accessToken = token;
};

const selectAccount = () => {
    /**
     * See here for more information on account retrieval:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */

    const currentAccounts = myMSALObj.getAllAccounts();

    if (currentAccounts.length < 1) {
        return;
    } else if (currentAccounts.length > 1) {
        /**
         * Due to the way MSAL caches account objects, the auth response from initiating a user-flow
         * is cached as a new account, which results in more than one account in the cache. Here we make
         * sure we are selecting the account with homeAccountId that contains the sign-up/sign-in user-flow,
         * as this is the default flow the user initially signed-in with.
         */
        // @ts-ignore
        const accounts = currentAccounts.filter((account) =>
            account.homeAccountId.toUpperCase().includes(b2cPolicies.names.signUpSignIn.toUpperCase())
            &&
            // @ts-ignore
            account.idTokenClaims.iss.toUpperCase().includes(b2cPolicies.authorityDomain.toUpperCase())
            &&
            // @ts-ignore
            account.idTokenClaims.aud === msalConfig.auth.clientId,
            );

        if (accounts.length > 1) {
            // localAccountId identifies the entity for which the token asserts information.
            if (accounts.every((account) => account.localAccountId === accounts[0].localAccountId)) {
                // All accounts belong to the same user
                setAccount(accounts[0]);
            } else {
                // Multiple users detected. Logout all to be safe.
                signOut();
            }
        } else if (accounts.length === 1) {
            setAccount(accounts[0]);
        }

    } else if (currentAccounts.length === 1) {
        setAccount(currentAccounts[0]);
    }
};

// in case of page refresh
selectAccount();


const signIn = () => {
    /**
     * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
     */

    myMSALObj.loginRedirect(loginRequest);
};

const signOut = () => {
    /**
     * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
     */

    const logoutRequest = {
        postLogoutRedirectUri: msalConfig.auth.redirectUri,
    };

    myMSALObj.logoutRedirect(logoutRequest);
};

const getTokenRedirect = async () => {
    /**
     * See here for more info on account retrieval:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */
    const request: SilentRequest = {
        ...tokenRequest,
        account: myMSALObj.getAccountByHomeId(accountId) as AccountInfo,
    };

    try {
        // In case the response from B2C server has an empty accessToken field
        // throw an error to initiate token acquisition
        const response = await myMSALObj.acquireTokenSilent(request);
        if (!response.accessToken || response.accessToken === '') {
            throw new InteractionRequiredAuthError();
        } else {
            console.log('access_token acquired at: ' + new Date().toString());
            accessToken = response.accessToken;
            return accessToken;
        }
    } catch (error) {
        console.log('Silent token acquisition fails. Acquiring token using redirect. \n', error);
        if (error instanceof InteractionRequiredAuthError) {
            // fallback to interaction when silent call fails
            return myMSALObj.acquireTokenRedirect(request);
        } else {
            console.log(error);
        }
    }
};

const checkToken = async () => {
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/token-lifetimes.md#token-renewal
    // this will use the access token if cached and use the refresh token if needed

    const currentAccount = myMSALObj.getAccountByHomeId(accountId) as AccountInfo;
    const silentRequest: SilentRequest = {
        ...tokenRequest,
        account: currentAccount,
    };
    const request = {
        ...loginRequest,
        loginHint: currentAccount.username,
    };

    const tokenResponse = await myMSALObj.acquireTokenSilent(silentRequest).catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
            // fallback to interaction when silent call fails
            return;
        }
    });
    if (!tokenResponse || !tokenResponse.accessToken || tokenResponse.accessToken === '') {
        autoLogout();
    } else {
        // console.log('access_token acquired at: ' + new Date().toString());
        accessToken = tokenResponse.accessToken;
        return accessToken;
    }
};

const autoLogout = () => {
    if (store) {
        store.dispatch('setIsLoggedIn', false);
    }
};


export default {
    async checkAccount() {
        if (accountId && accountId !== '') {
            await checkToken(); // logs out if access and refresh token are not good
        }

        if (store && accessToken && accessToken.length > 0) {
            store.dispatch('setIsLoggedIn', true);
            store.dispatch('initFavourites');
            store.dispatch('initFilters');
        }
    },
    async getAccessToken() {
        await checkToken(); // logs out if access and refresh token are not good

        if (accessToken && accessToken.length > 0) {
            return accessToken;
        }

        return '';
    },
    login() {
        signIn();
    },
    logout() {
        signOut();
    },
};
